import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { AlertTriangle, Shield, CheckCircle } from 'lucide-react';

interface ZoneRisk {
    id: string;
    name: string;
    riskLevel: 'low' | 'medium' | 'high';
    score: number;
    lastAudit: string;
}

interface RiskHeatMapProps {
    zones: ZoneRisk[];
    title?: string;
}

const riskConfig = {
    low: {
        color: 'bg-safety-100 border-safety-300',
        textColor: 'text-safety-700',
        icon: CheckCircle,
        label: 'Low Risk',
    },
    medium: {
        color: 'bg-alert-100 border-alert-300',
        textColor: 'text-alert-700',
        icon: AlertTriangle,
        label: 'Medium Risk',
    },
    high: {
        color: 'bg-red-100 border-red-300',
        textColor: 'text-red-700',
        icon: AlertTriangle,
        label: 'High Risk',
    },
};

/**
 * RiskHeatMap - Visual grid showing risk levels across zones
 * Features color-coded tiles with hover details
 */
export default function RiskHeatMap({
    zones,
    title = 'Zone Risk Assessment',
}: RiskHeatMapProps) {
    const { ref, isInView } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });
    const [selectedZone, setSelectedZone] = useState<ZoneRisk | null>(null);

    const riskCounts = {
        low: zones.filter(z => z.riskLevel === 'low').length,
        medium: zones.filter(z => z.riskLevel === 'medium').length,
        high: zones.filter(z => z.riskLevel === 'high').length,
    };

    return (
        <div
            ref={ref}
            className={`card-glass p-6 ${isInView ? 'animate-fadeIn' : 'opacity-0'}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-charcoal-100 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-charcoal-600" />
                    </div>
                    <div>
                        <h3 className="font-display font-semibold text-charcoal-900">{title}</h3>
                        <p className="text-sm text-steel-500">{zones.length} zones monitored</p>
                    </div>
                </div>

                {/* Risk Summary */}
                <div className="flex gap-4">
                    {(['low', 'medium', 'high'] as const).map(level => {
                        const config = riskConfig[level];
                        return (
                            <div key={level} className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${config.color.split(' ')[0].replace('100', '500')}`} />
                                <span className="text-sm text-steel-600">
                                    {riskCounts[level]} {config.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Heat Map Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {zones.map((zone, index) => {
                    const config = riskConfig[zone.riskLevel];
                    const Icon = config.icon;
                    const isSelected = selectedZone?.id === zone.id;

                    return (
                        <button
                            key={zone.id}
                            onClick={() => setSelectedZone(isSelected ? null : zone)}
                            className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${config.color} ${isSelected ? 'ring-2 ring-offset-2 ring-ember-500 scale-105' : 'hover:scale-102'
                                }`}
                            style={{
                                animationDelay: `${index * 50}ms`,
                            }}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <span className={`text-sm font-semibold ${config.textColor}`}>
                                    {zone.name}
                                </span>
                                <Icon className={`w-4 h-4 ${config.textColor}`} />
                            </div>
                            <div className="text-xs text-steel-600">
                                Score: {zone.score}/100
                            </div>
                            <div className="text-xs text-steel-500 mt-1">
                                Last audit: {zone.lastAudit}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Selected Zone Details */}
            {selectedZone && (
                <div className="mt-6 p-4 bg-steel-50 rounded-xl border border-steel-200 animate-fadeIn">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-semibold text-charcoal-900">{selectedZone.name}</h4>
                            <p className="text-sm text-steel-600">
                                Risk Score: <span className="font-medium">{selectedZone.score}/100</span>
                            </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${riskConfig[selectedZone.riskLevel].color} ${riskConfig[selectedZone.riskLevel].textColor}`}>
                            {riskConfig[selectedZone.riskLevel].label}
                        </div>
                    </div>
                    <p className="text-sm text-steel-500 mt-2">
                        Last audited on {selectedZone.lastAudit}. Click to view detailed report.
                    </p>
                </div>
            )}
        </div>
    );
}
